import { IntlShape } from 'react-intl';
import * as Yup from 'yup';

import {
  CoachingTopicObject,
  CoachingTopicType,
  ExtendMetricObject,
  StatusOptionsObject,
} from '../../../api/CoachingForm/schema';
import { COACHING_TEXT_LIMIT } from '../../../Util';

const validationSchema = (intl: IntlShape): Yup.ObjectSchema => {
  const constraints = { tenets: { max: 2 }, behaviors: { max: 3 } };
  const messages = {
    coachingTopic: {
      required: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_validate_required_topic',
        defaultMessage: 'Please choose a topic from the dropdown.',
      }),
    },
    relatedContacts: {
      required: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_validate_required_contacts',
        defaultMessage: 'A contact is required for this coaching detail',
      }),
    },
    tenets: {
      required: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_validate_required_tenets',
        defaultMessage: 'A tenet is required for this coaching detail',
      }),
      max: intl.formatMessage(
        {
          id: 'cs_perfportal_coaching_detail_validate_max_tenets',
          defaultMessage: 'No more than {0} tenets are allowed',
        },
        { 0: constraints.tenets.max }
      ),
    },
    behaviors: {
      required: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_validate_required_behaviours',
        defaultMessage: 'A behavior is required for this coaching detail',
      }),
      max: intl.formatMessage(
        {
          id: 'cs_perfportal_coaching_detail_validate_max_behaviours',
          defaultMessage: 'No more than {0} behaviors are allowed.',
        },
        { 0: constraints.behaviors.max }
      ),
    },
    relatedMetric: {
      required: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_validate_required_metric',
        defaultMessage: 'A metric is required for this coaching detail',
      }),
    },
    assignedAction: {
      required: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_validate_required_action',
        defaultMessage: 'An action is required for this coaching detail',
      }),
    },
  };

  return Yup.object().shape({
    coachingTopic: Yup.string()
      .nullable()
      .required(messages.coachingTopic.required)
      .oneOf(
        Object.values(CoachingTopicObject),
        messages.coachingTopic.required
      ),
    relatedContacts: Yup.array().when(['coachingTopic', 'status'], {
      is: (topic, status) =>
        topic === CoachingTopicObject.QUALITY &&
        status === StatusOptionsObject.PENDING,
      then: Yup.array().required(messages.relatedContacts.required),
    }),
    tenets: Yup.array()
      .when('coachingTopic', {
        is: CoachingTopicObject.QUALITY,
        then: Yup.array()
          // @TODO Need a way for users to resolve the following, if we intend to enforce it.
          // .of(Yup.string().oneOf(Object.values(TenetObject)))
          .required(messages.tenets.required),
      })
      .max(constraints.tenets.max, messages.tenets.max),
    behaviors: Yup.array()
      .when('coachingTopic', {
        is: CoachingTopicObject.QUALITY,
        then: Yup.array()
          // @TODO Need a way for users to resolve the following, if we intend to enforce it.
          // .of(Yup.string().oneOf(Object.Values(BehaviorObject)))
          .required(messages.behaviors.required),
      })
      .max(constraints.behaviors.max, messages.behaviors.max),
    relatedMetric: Yup.string()
      .nullable()
      .when(['coachingTopic', 'status'], {
        is: (value: CoachingTopicType, status) =>
          value !== CoachingTopicObject.DEVELOPMENT &&
          status === StatusOptionsObject.PENDING,
        then: Yup.string().oneOf(
          Object.keys(ExtendMetricObject()),
          messages.relatedMetric.required
        ),
      }),
    assignedAction: Yup.string()
      .max(COACHING_TEXT_LIMIT, '-')
      .required(messages.assignedAction.required),
  });
};

export default validationSchema;
